import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Budget = () => {
  const { t, i18n } = useTranslation();
  const { wedding, setWedding, addBudget, updateBudget, removeBudget, currency, setCurrency } = useAppContext();
  const [item, setItem] = useState({ name: '', amount: '' });
  const [editingIdx, setEditingIdx] = useState(null);
  const [editItem, setEditItem] = useState({ name: '', amount: '' });
  const [budgets, setBudgets] = useState([]);
  const cardId = localStorage.getItem('id');

  useEffect(() => {
    const storedCurrency = localStorage.getItem('budgetCurrency');
    if (storedCurrency) {
      setCurrency(storedCurrency);
    }

    const fetchBudgets = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/planner/budgets?card_id=${cardId}`);
        setBudgets(response.data);
      } catch (error) {
        console.error("Could not fetch budgets:", error);
      }
    };

    fetchBudgets();
  }, [cardId, setCurrency]);

  const handleCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setCurrency(newCurrency);
    localStorage.setItem('budgetCurrency', newCurrency);
  };

  const handleAdd = async e => {
    e.preventDefault();
    if (item.name.trim() && item.amount) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/planner/budgets`, { item: item.name, amount: parseFloat(item.amount), card_id: cardId });
        setBudgets([...budgets, response.data]);
        setItem({ name: '', amount: '' });
      } catch (error) {
        console.error("Could not add budget:", error);
      }
    }
  };

  const handleEdit = idx => {
    setEditingIdx(idx);
    setEditItem(budgets[idx]);
  };

  const handleEditSave = async idx => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/planner/budgets/${budgets[idx].id}`, { item: editItem.item, amount: parseFloat(editItem.amount), card_id: cardId });
      const newBudgets = [...budgets];
      newBudgets[idx] = response.data;
      setBudgets(newBudgets);
      setEditingIdx(null);
      setEditItem({ name: '', amount: '' });
    } catch (error) {
      console.error("Could not update budget:", error);
    }
  };

  const handleClearData = async () => {
    if (window.confirm(t('budget.confirmClearAll'))) {
      try {
        // Assuming you have an API endpoint to clear all budgets for a card_id
        // You might need to add this to your PlannerController and api.php
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/budgets/clear-all/${cardId}`);
        setBudgets([]);
      } catch (error) {
        console.error("Could not clear all budgets:", error);
      }
    }
  };

  const handleRemove = async idx => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/budgets/${budgets[idx].id}`);
      const newBudgets = [...budgets];
      newBudgets.splice(idx, 1);
      setBudgets(newBudgets);
    } catch (error) {
      console.error("Could not delete budget:", error);
    }
  };

  const total = budgets.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const navigate = useNavigate();

  return (
    <div className="w-full max-w-4xl mx-auto py-10 px-2 sm:px-4 overflow-x-hidden">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary">{t('budget.title')}</h1>
        <div className="flex items-center gap-2">
          <label htmlFor="currency-select" className="text-foreground/70 text-sm">{t('budget.currencyLabel')}</label>
          <select
            id="currency-select"
            className="border rounded px-2 py-1 text-sm"
            value={currency}
            onChange={handleCurrencyChange}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
      </div>
      <p className="text-foreground/70 mb-2">{t('budget.description')}</p>
      <p className="text-sm text-foreground/50 italic mb-4">
        {t('budget.sampleDataNote')}
      </p>

      {/* Responsive form & clear button */}
      <div className="flex flex-wrap md:flex-nowrap gap-3 mb-6">
        <form className="flex flex-wrap gap-3 flex-1 min-w-0" onSubmit={handleAdd}>
          <input
            className="flex-1 border rounded px-3 py-2 text-base sm:text-lg min-w-[140px]"
            placeholder={t('budget.expenseNamePlaceholder')}
            value={item.name}
            onChange={e => setItem(i => ({ ...i, name: e.target.value }))}
          />
          <input
            className="w-full sm:w-32 border rounded px-3 py-2"
            placeholder={t('budget.amountPlaceholder')}
            type="number"
            min="0"
            value={item.amount}
            onChange={e => setItem(i => ({ ...i, amount: e.target.value }))}
          />
          <button
            type="submit"
            className="bg-primary text-white px-4 py-2 rounded hover:bg-secondary hover:text-gray-800 transition-colors w-full sm:w-auto"
          >
            {t('common.add')}
          </button>
        </form>

        <button
          onClick={handleClearData}
          className="bg-danger text-white px-4 py-2 rounded hover:bg-secondary hover:text-gray-800 transition-colors w-full md:w-auto"
          disabled={budgets.length === 0}
        >
          {t('budget.clearAllData')}
        </button>
      </div>

      {/* Budget Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">{t('budget.nameHeader')}</th>
              <th className="px-4 py-2 text-left">{t('budget.amountHeader')}</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {budgets.length === 0 && (
              <tr>
                <td colSpan={3} className="text-foreground/60 italic px-4 py-2">
                  {t('budget.noExpensesYet')}
                </td>
              </tr>
            )}
            {budgets.map((i, idx) => (
              <tr key={idx} className="border-t">
                {editingIdx === idx ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        className="border rounded px-2 py-1 w-full"
                        value={editItem.item}
                        onChange={e =>
                          setEditItem(ev => ({ ...ev, item: e.target.value }))
                        }
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="border rounded px-2 py-1 w-full"
                        type="number"
                        value={editItem.amount}
                        onChange={e =>
                          setEditItem(ev => ({ ...ev, amount: e.target.value }))
                        }
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <button
                        onClick={() => handleEditSave(idx)}
                        className="text-success font-bold px-2"
                      >
                        {t('common.save')}
                      </button>
                      <button
                        onClick={() => setEditingIdx(null)}
                        className="text-danger px-2"
                      >
                        {t('common.cancel')}
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2">{i.item}</td>
                    <td className="px-4 py-2">{formatCurrency(i.amount)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(idx)}
                        className="text-info px-2"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleRemove(idx)}
                        className="text-danger px-2"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold">
              <td className="px-4 py-2 text-left" colSpan={1}>
                {t('budget.total')}
              </td>
              <td className="px-4 py-2">{formatCurrency(total)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default Budget;
